import { useEffect, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { Building2, ChevronLeft, ChevronRight, Ellipsis, Mail, MapPin, Pencil, Phone, Plus, Search, ShieldCheck, Users, X } from 'lucide-react';
import './FacilitiesAdmin.css';
import FacilityForm, { type FacilityFormData } from './FacilityForm';
import {
  addFacilityMember,
  createFacility,
  getFacilityMembers,
  getFacilities,
  searchAdminFacilities,
  updateFacility,
  updateFacilityOwned,
  updateFacilityVerification,
  type AccountRole,
  type Facility,
  type FacilityType,
} from '../lib/api';

const facilityTypeLabels: Record<FacilityType, string> = {
  VETERINARY_CLINIC: 'Veterinary Clinic',
  BOARDING_FACILITY: 'Boarding Facility',
  TRAINING_FACILITY: 'Training Facility',
  OTHER: 'Other',
};

export default function FacilitiesPage({ role }: { role: AccountRole }) {
  const { getAccessTokenSilently } = useAuth0();
  const isSuperAdmin = role === 'SUPER_ADMIN';
  const [facilities, setFacilities] = useState<Facility[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [editingFacility, setEditingFacility] = useState<Facility | null>(null);
  const [searchText, setSearchText] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [facilityTypeFilter, setFacilityTypeFilter] = useState<FacilityType | 'all'>('all');
  const [verificationFilter, setVerificationFilter] = useState<'all' | 'verified' | 'unverified'>('all');
  const [page, setPage] = useState(1);
  const [totalFacilities, setTotalFacilities] = useState(0);
  const pageSize = 50;
  const [selectedFacility, setSelectedFacility] = useState<Facility | null>(null);
  const [memberships, setMemberships] = useState<Awaited<ReturnType<typeof getFacilityMembers>> | null>(null);
  const [memberEmail, setMemberEmail] = useState('');
  const [memberMessage, setMemberMessage] = useState('');
  const [savingMember, setSavingMember] = useState(false);
  const [verifyingFacilityId, setVerifyingFacilityId] = useState('');
  const [owningFacilityId, setOwningFacilityId] = useState('');
  const [toggleNotice, setToggleNotice] = useState<{ facilityId: string; message: string } | null>(null);

  const loadFacilities = async (requestedPage = page, requestedQuery = searchQuery, requestedType = facilityTypeFilter, requestedVerification = verificationFilter) => {
    setLoading(true);
    setError('');
    try {
      const token = await getAccessTokenSilently();
      if (!token) throw new Error('Missing access token');
      if (isSuperAdmin) {
        const result = await searchAdminFacilities(token, requestedQuery, requestedPage, pageSize, requestedType, requestedVerification);
        setFacilities(result.facilities);
        setTotalFacilities(result.total);
      } else {
        const result = await getFacilities(token, false);
        setFacilities(result);
        setTotalFacilities(result.length);
      }
    } catch (loadError) {
      setError(loadError instanceof Error ? loadError.message : 'Unable to load facilities');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const timeout = window.setTimeout(() => {
      setSearchQuery(searchText.trim());
      setPage(1);
    }, 250);
    return () => window.clearTimeout(timeout);
  }, [searchText]);

  useEffect(() => { void loadFacilities(page, searchQuery, facilityTypeFilter, verificationFilter); }, [getAccessTokenSilently, isSuperAdmin, page, searchQuery, facilityTypeFilter, verificationFilter]);

  const openTeam = async (facility: Facility) => {
    setSelectedFacility(facility);
    setMemberships(null);
    setMemberMessage('');
    try {
      const token = await getAccessTokenSilently();
      if (!token) throw new Error('Missing access token');
      setMemberships(await getFacilityMembers(token, facility.id));
    } catch (loadError) {
      setError(loadError instanceof Error ? loadError.message : 'Unable to load team');
    }
  };

  const addMember = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!selectedFacility || !memberEmail.trim()) return;
    setSavingMember(true);
    setMemberMessage('');
    setError('');
    try {
      const token = await getAccessTokenSilently();
      if (!token) throw new Error('Missing access token');
      const result = await addFacilityMember(token, selectedFacility.id, memberEmail);
      setMemberMessage(result.status === 'added' ? 'User added to the facility.' : 'Invitation saved. They will be added when they next sign in with this email.');
      setMemberEmail('');
      setMemberships(await getFacilityMembers(token, selectedFacility.id));
    } catch (addError) {
      setError(addError instanceof Error ? addError.message : 'Unable to add user');
    } finally {
      setSavingMember(false);
    }
  };

  const saveFacility = async (data: FacilityFormData) => {
    const token = await getAccessTokenSilently();
    if (!token) throw new Error('Missing access token');
    if (editingFacility) {
      await updateFacility(token, editingFacility.id, {
        name: data.name,
        type: data.type,
        contact_name: data.contact_name || null,
        email: data.email || null,
        phone: data.phone || null,
        website: data.website || null,
        address_line1: data.address_line1,
        address_line2: data.address_line2 || null,
        city: data.city,
        region: data.region,
        postal_code: data.postal_code,
        veterinarians: data.veterinarians,
      });
    } else {
      await createFacility(token, data);
    }
    setShowCreate(false);
    setEditingFacility(null);
    await loadFacilities(page, searchQuery);
  };

  const toggleVerification = async (facility: Facility) => {
    setVerifyingFacilityId(facility.id);
    setToggleNotice(null);
    try {
      const token = await getAccessTokenSilently();
      if (!token) throw new Error('Missing access token');
      const updated = await updateFacilityVerification(token, facility.id, !facility.verified_at);
      setFacilities((current) => current.map((item) => item.id === updated.id ? { ...item, verified_at: updated.verified_at } : item));
    } catch (verifyError) {
      setToggleNotice({
        facilityId: facility.id,
        message: verifyError instanceof Error ? verifyError.message : 'Unable to update verification',
      });
    } finally {
      setVerifyingFacilityId('');
    }
  };

  const toggleOwned = async (facility: Facility) => {
    if (!facility.owned && !facility.email?.trim()) {
      setToggleNotice({
        facilityId: facility.id,
        message: 'A facility email is required before marking it owned.',
      });
      return;
    }
    setOwningFacilityId(facility.id);
    setToggleNotice(null);
    try {
      const token = await getAccessTokenSilently();
      if (!token) throw new Error('Missing access token');
      const updated = await updateFacilityOwned(token, facility.id, !facility.owned);
      setFacilities((current) => current.map((item) => item.id === updated.id ? { ...item, owned: updated.owned } : item));
    } catch (ownedError) {
      setToggleNotice({
        facilityId: facility.id,
        message: ownedError instanceof Error ? ownedError.message : 'Unable to update ownership',
      });
    } finally {
      setOwningFacilityId('');
    }
  };

  return <>
    <div className="facility-page-heading"><div><p className="eyebrow">{isSuperAdmin ? 'Administration' : 'Account'}</p><h1>{isSuperAdmin ? 'Facilities' : 'My facility'}</h1><p>{isSuperAdmin ? 'Manage facility accounts and facility teams.' : 'Manage the people who can access your facility workspace.'}</p></div>{isSuperAdmin && <button className="button button-primary" onClick={() => { setEditingFacility(null); setShowCreate(true); }}><Plus size={17} /> Add facility</button>}</div>
    {error && <p className="form-error facility-error" role="alert">{error}</p>}
    {isSuperAdmin && <div className="facility-admin-toolbar"><label className="facility-search"><Search size={16} /><input type="search" value={searchText} onChange={(event) => setSearchText(event.target.value)} placeholder="Search name, address, city, ZIP, or email" aria-label="Search facilities" /></label><label className="facility-type-filter">Facility type<select aria-label="Filter by facility type" value={facilityTypeFilter} onChange={(event) => { setFacilityTypeFilter(event.target.value as FacilityType | 'all'); setPage(1); }}><option value="all">All types</option>{Object.entries(facilityTypeLabels).map(([value, label]) => <option key={value} value={value}>{label}</option>)}</select></label><div className="facility-verification-filter" role="group" aria-label="Filter by verification status">{(['all', 'verified', 'unverified'] as const).map((filter) => <button type="button" key={filter} className={verificationFilter === filter ? 'active' : ''} aria-pressed={verificationFilter === filter} onClick={() => { setVerificationFilter(filter); setPage(1); }}>{filter[0].toUpperCase() + filter.slice(1)}</button>)}</div><span className="facility-result-count">{loading ? 'Searching...' : `${totalFacilities.toLocaleString()} ${totalFacilities === 1 ? 'facility' : 'facilities'}`}</span></div>}
    {loading ? <p className="facility-empty">Loading facilities...</p> : facilities.length === 0 ? <p className="facility-empty">{isSuperAdmin ? (searchQuery ? `No facilities match “${searchQuery}”.` : 'No facilities have been created yet.') : 'No facility has been assigned to your account yet.'}</p> : <div className="facility-list">{facilities.map((facility) => <section className="facility-row" key={facility.id}>
      <div className="facility-card-header">
        <div className="facility-mark"><Building2 size={20} /></div>
        <div className="facility-copy">
          <div className="facility-title"><h2>{facility.name}</h2><span className="tag">{facilityTypeLabels[facility.type]}</span>{facility.verified_at ? <span className="verified-badge"><ShieldCheck size={13} /> Verified</span> : <span className="unverified-badge">Unverified</span>}</div>
        </div>
        {isSuperAdmin && <div className="facility-switches">
          <label className="verification-control"><span>Owned</span><button type="button" role="switch" className={`verification-switch ${facility.owned ? 'on' : ''}`} aria-checked={facility.owned} aria-label={`${facility.owned ? 'Mark unowned' : 'Mark owned'} ${facility.name}`} title={facility.email?.trim() ? undefined : 'Add a facility email before marking it owned'} disabled={owningFacilityId === facility.id} onClick={() => void toggleOwned(facility)}><i /></button></label>
          <label className="verification-control"><span>Verified</span><button type="button" role="switch" className={`verification-switch ${facility.verified_at ? 'on' : ''}`} aria-checked={Boolean(facility.verified_at)} aria-label={`${facility.verified_at ? 'Unverify' : 'Verify'} ${facility.name}`} disabled={verifyingFacilityId === facility.id} onClick={() => void toggleVerification(facility)}><i /></button></label>
          {toggleNotice?.facilityId === facility.id && <p className="facility-toggle-notice error" role="alert">{toggleNotice.message}</p>}
        </div>}
        <details className="facility-action-menu">
          <summary aria-label={`Actions for ${facility.name}`} title="Facility actions"><Ellipsis size={20} /></summary>
          <div className="facility-action-menu-items">
            {isSuperAdmin && <button type="button" onClick={(event) => { event.currentTarget.closest('details')?.removeAttribute('open'); setEditingFacility(facility); setShowCreate(true); }}><Pencil size={15} /> Edit</button>}
            <button type="button" onClick={(event) => { event.currentTarget.closest('details')?.removeAttribute('open'); void openTeam(facility); }}><Users size={15} /> Manage users</button>
          </div>
        </details>
      </div>
      <div className="facility-row-secondary">
        <a className="facility-address-link" href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent([facility.address_line1, facility.address_line2, facility.city, facility.region, facility.postal_code].filter(Boolean).join(', '))}`} target="_blank" rel="noreferrer"><MapPin size={13} /> {[facility.address_line1, facility.address_line2, facility.city, facility.region, facility.postal_code].filter(Boolean).join(', ')}</a>
        <div className="facility-meta">{facility.phone && <span><Phone size={13} /> {facility.phone}</span>}{facility.email && <span><Mail size={13} /> {facility.email}</span>}<span>{facility.veterinarians.length} {facility.veterinarians.length === 1 ? 'veterinarian' : 'veterinarians'}</span></div>
        {facility.veterinarians.length > 0 && <div className="facility-vets">{facility.veterinarians.map((veterinarian) => <span className="tag" key={veterinarian.id || veterinarian.name}>{veterinarian.name}{veterinarian.license_number ? ` · ${veterinarian.license_number}` : ''}</span>)}</div>}
      </div>
      {selectedFacility?.id === facility.id && <div className="facility-team-panel"><div className="facility-team-heading"><h3>Team members</h3><button className="icon-button" onClick={() => { setSelectedFacility(null); setMemberships(null); }} aria-label="Close team panel"><X size={17} /></button></div>{memberships ? <><div className="facility-member-list">{memberships.memberships.map((membership) => <div className="facility-member" key={membership.id}><div><strong>{membership.owner.name}</strong><span>{membership.owner.email}</span></div><span className="tag">{membership.role === 'ACCOUNT_OWNER' ? 'Account Owner' : 'User'}</span></div>)}{memberships.invitations.map((invitation) => <div className="facility-member pending" key={invitation.id}><div><strong>{invitation.email}</strong><span>Pending sign-in</span></div><span className="tag">{invitation.role === 'ACCOUNT_OWNER' ? 'Account Owner' : 'User'}</span></div>)}</div><form className="facility-add-member" onSubmit={addMember}><label>Email address<input type="email" value={memberEmail} onChange={(event) => setMemberEmail(event.target.value)} required placeholder="teammate@example.com" /></label><button className="button button-secondary" disabled={savingMember}><Plus size={15} /> {savingMember ? 'Adding...' : 'Add user'}</button></form>{memberMessage && <p className="facility-success" role="status">{memberMessage}</p>}</> : <p className="facility-empty">Loading team...</p>}</div>}
    </section>)}</div>}
    {isSuperAdmin && totalFacilities > pageSize && <nav className="facility-pagination" aria-label="Facility pages"><span>Showing {(page - 1) * pageSize + 1}–{Math.min(page * pageSize, totalFacilities)} of {totalFacilities.toLocaleString()}</span><div><button type="button" className="button button-secondary" disabled={page <= 1 || loading} onClick={() => setPage((current) => Math.max(1, current - 1))}><ChevronLeft size={16} /> Previous</button><span>Page {page} of {Math.ceil(totalFacilities / pageSize)}</span><button type="button" className="button button-secondary" disabled={page >= Math.ceil(totalFacilities / pageSize) || loading} onClick={() => setPage((current) => current + 1)}>Next <ChevronRight size={16} /></button></div></nav>}
    {showCreate && <FacilityForm key={editingFacility?.id ?? 'new-facility'} mode="admin" initialFacility={editingFacility ?? undefined} onClose={() => { setShowCreate(false); setEditingFacility(null); }} onSave={saveFacility} />}
  </>;
}
